import React, { useCallback, useEffect, useState } from "react";
import { CreditCard, Wallet, Pencil, Plus, Trash2, History } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../../lib/api";
import { fmtDateTime } from "../../../lib/time";
import { useAuth } from "../../../context/AuthContext";
import { Label, StatCard, EmptyState } from "../../../components/common";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import { PlanChangeHistory, PlanHistoryDialog } from "../../../components/PlanChangeHistory";

export default function PlatformSubscriptions() {
  const { user } = useAuth();
  const canManage = ["master_admin", "platform_sales"].includes(user.role);
  const isMaster = user.role === "master_admin";
  const [d, setD] = useState(null);
  const [plans, setPlans] = useState([]);
  const [history, setHistory] = useState([]);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({ plan_id: "", mrr: "", status: "active" });
  const [planEdit, setPlanEdit] = useState(null); // {id?} or "new"
  const [planForm, setPlanForm] = useState({ name: "", price: "", features: "", max_locations: "", max_employees: "" });
  const [historyPlan, setHistoryPlan] = useState(null);
  const [planRev, setPlanRev] = useState(0);

  const load = useCallback(() => api.get("/platform/subscriptions").then((r) => setD(r.data)).catch((e) => console.warn("Subscriptions load failed", e)), []);
  const loadPlans = useCallback(() => api.get("/platform/plans").then((r) => setPlans(r.data)).catch((e) => console.warn("Plans load failed", e)), []);
  const loadHistory = useCallback(() => api.get("/platform/billing-history").then((r) => setHistory(r.data)).catch((e) => console.warn("Billing history load failed", e)), []);
  useEffect(() => { load(); loadPlans(); loadHistory(); }, [load, loadPlans, loadHistory]);

  const openEdit = (s) => {
    setEdit(s);
    setForm({ plan_id: s.plan_id || "", mrr: String(s.mrr ?? ""), status: s.status || "active" });
  };

  const onPlan = (pid) => {
    const p = plans.find((x) => x.id === pid);
    setForm((f) => ({ ...f, plan_id: pid, mrr: p ? String(p.price) : f.mrr }));
  };

  const save = async () => {
    try {
      await api.patch(`/platform/organizations/${edit.org_id}/subscription`, {
        plan_id: form.plan_id,
        mrr: form.mrr === "" ? null : Number(form.mrr),
        status: form.status,
      });
      toast.success("Subscription updated");
      setEdit(null);
      load(); loadHistory();
    } catch (e) { toast.error(apiError(e)); }
  };

  const openPlan = (p) => {
    setPlanEdit(p || "new");
    setPlanForm(p ? { name: p.name, price: String(p.price), features: (p.features || []).join("\n"),
                      max_locations: p.max_locations == null ? "" : String(p.max_locations),
                      max_employees: p.max_employees == null ? "" : String(p.max_employees) }
                  : { name: "", price: "", features: "", max_locations: "", max_employees: "" });
  };
  const savePlan = async () => {
    const payload = { name: planForm.name, price: Number(planForm.price),
                      features: planForm.features.split("\n").map((s) => s.trim()).filter(Boolean),
                      max_locations: planForm.max_locations === "" ? null : Number(planForm.max_locations),
                      max_employees: planForm.max_employees === "" ? null : Number(planForm.max_employees) };
    try {
      if (planEdit === "new") await api.post("/platform/plans", payload);
      else await api.patch(`/platform/plans/${planEdit.id}`, payload);
      toast.success("Plan saved"); setPlanEdit(null); loadPlans(); setPlanRev((n) => n + 1);
    } catch (e) { toast.error(apiError(e)); }
  };
  const deletePlan = async (p) => {
    try { await api.delete(`/platform/plans/${p.id}`); toast.success("Plan deleted"); loadPlans(); setPlanRev((n) => n + 1); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (!d) return <div className="text-slate-400">Loading…</div>;
  return (
    <div className="space-y-6">
      <div><Label>Platform</Label><h1 className="font-head text-2xl font-bold text-slate-900 sm:text-3xl">Tiers &amp; Billing</h1></div>
      <div className="grid gap-4 md:grid-cols-3">
        <StatCard label="Monthly Recurring Revenue" value={`R${d.mrr.toLocaleString()}`} accent="#10B981" testId="sub-mrr"><Wallet className="h-5 w-5 text-slate-300" /></StatCard>
        <StatCard label="Annual Run Rate" value={`R${d.arr.toLocaleString()}`} accent="#4338CA" testId="sub-arr" />
        <StatCard label="Active Subscriptions" value={d.active} testId="sub-active"><CreditCard className="h-5 w-5 text-slate-300" /></StatCard>
      </div>

      <div className="flex items-center justify-between">
        <Label>Plan catalogue</Label>
        {isMaster && <Button size="sm" variant="outline" onClick={() => openPlan(null)} data-testid="new-plan"><Plus className="mr-1 h-4 w-4" /> New plan</Button>}
      </div>
      <div className="grid gap-3 sm:grid-cols-3">
        {plans.map((p) => (
          <div key={p.id} className="relative rounded-xl border border-slate-200 bg-white p-5" data-testid={`plan-${p.id}`}>
            {isMaster && (
              <div className="absolute right-3 top-3 flex gap-1">
                <button onClick={() => setHistoryPlan(p)} data-testid={`history-plan-${p.id}`} title="Change history" className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"><History className="h-4 w-4" /></button>
                <button onClick={() => openPlan(p)} data-testid={`edit-plan-${p.id}`} className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"><Pencil className="h-4 w-4" /></button>
                <button onClick={() => deletePlan(p)} data-testid={`del-plan-${p.id}`} className="rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
              </div>
            )}
            <p className="font-head text-lg font-semibold text-slate-900">{p.name}</p>
            <p className="sv-metric mt-1 text-2xl font-bold text-[#4338CA]">R{p.price}<span className="text-sm text-slate-400">/{p.interval}</span></p>
            <div className="mt-2 flex gap-2 text-xs font-semibold">
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-600">{p.max_locations == null ? "∞" : p.max_locations} locations</span>
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-600">{p.max_employees == null ? "∞" : p.max_employees} employees</span>
            </div>
            <ul className="mt-3 space-y-1 text-sm text-slate-500">{p.features.map((f) => <li key={f}>· {f}</li>)}</ul>
          </div>
        ))}
      </div>

      {isMaster && <PlanChangeHistory plans={plans} refreshKey={planRev} />}
      <PlanHistoryDialog plan={historyPlan} onClose={() => setHistoryPlan(null)} />

      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <Label>Active organisations</Label>
        {d.subscriptions.length === 0 ? <div className="mt-2"><EmptyState icon={CreditCard} title="No subscriptions" /></div> : (
          <div className="mt-3 overflow-x-auto sv-scroll">
            <table className="w-full min-w-[560px] text-sm" data-testid="sub-table">
              <thead><tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-400"><th className="py-2">Organisation</th><th>Plan</th><th>MRR</th><th>Status</th>{canManage && <th></th>}</tr></thead>
              <tbody>
                {d.subscriptions.map((s) => (
                  <tr key={s.id} className="border-b border-slate-100" data-testid={`sub-row-${s.org_id}`}>
                    <td className="py-3 font-semibold text-slate-800">{s.organization_name}</td>
                    <td className="text-slate-600">{s.plan_name}</td>
                    <td className="sv-metric font-bold text-slate-800">R{s.mrr}</td>
                    <td><span className={`rounded-full px-2 py-0.5 text-xs font-bold ${s.status === "active" ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-500"}`}>{s.status}</span></td>
                    {canManage && (
                      <td className="text-right">
                        <Button size="sm" variant="outline" onClick={() => openEdit(s)} data-testid={`manage-plan-${s.org_id}`}>
                          <Pencil className="mr-1 h-3.5 w-3.5" /> Manage
                        </Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <div className="flex items-center gap-2"><History className="h-4 w-4 text-slate-400" /><Label>Billing history</Label></div>
        {history.length === 0 ? <p className="mt-3 py-6 text-center text-sm text-slate-400">No plan changes recorded yet.</p> : (
          <div className="mt-3 overflow-x-auto sv-scroll">
            <table className="w-full min-w-[720px] text-sm" data-testid="billing-history-table">
              <thead><tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-400">
                <th className="py-2">Date</th><th>Organisation</th><th>Change</th><th>MRR</th><th>Status</th><th>By</th></tr></thead>
              <tbody>
                {history.map((h) => (
                  <tr key={h.id} className="border-b border-slate-100" data-testid={`billing-${h.id}`}>
                    <td className="py-3 text-slate-400">{fmtDateTime(h.created_at)}</td>
                    <td className="font-semibold text-slate-800">{h.organization_name}</td>
                    <td className="text-slate-600">{h.from_plan ? `${h.from_plan} → ` : ""}<b>{h.to_plan}</b></td>
                    <td className="sv-metric text-slate-700">{h.from_mrr != null ? `R${h.from_mrr} → ` : ""}R{h.to_mrr}</td>
                    <td className="text-slate-500">{h.to_status}</td>
                    <td className="text-slate-400">{h.actor || h.actor_email}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={!!planEdit} onOpenChange={(o) => !o && setPlanEdit(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{planEdit === "new" ? "New plan" : "Edit plan"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="mb-1 block normal-case tracking-normal text-slate-600">Plan name</Label>
              <Input value={planForm.name} onChange={(e) => setPlanForm({ ...planForm, name: e.target.value })} data-testid="plan-name" />
            </div>
            <div>
              <Label className="mb-1 block normal-case tracking-normal text-slate-600">Monthly price (ZAR)</Label>
              <Input type="number" value={planForm.price} onChange={(e) => setPlanForm({ ...planForm, price: e.target.value })} data-testid="plan-price" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="mb-1 block normal-case tracking-normal text-slate-600">Max locations</Label>
                <Input type="number" min="0" placeholder="Unlimited" value={planForm.max_locations}
                  onChange={(e) => setPlanForm({ ...planForm, max_locations: e.target.value })} data-testid="plan-max-locations" />
              </div>
              <div>
                <Label className="mb-1 block normal-case tracking-normal text-slate-600">Max employees</Label>
                <Input type="number" min="0" placeholder="Unlimited" value={planForm.max_employees}
                  onChange={(e) => setPlanForm({ ...planForm, max_employees: e.target.value })} data-testid="plan-max-employees" />
              </div>
            </div>
            <p className="-mt-1 text-xs text-slate-400">Leave a limit blank for unlimited (Enterprise).</p>
            <div>
              <Label className="mb-1 block normal-case tracking-normal text-slate-600">Features (one per line)</Label>
              <textarea value={planForm.features} onChange={(e) => setPlanForm({ ...planForm, features: e.target.value })}
                data-testid="plan-features" rows={5} className="w-full rounded-md border border-slate-200 p-2 text-sm" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPlanEdit(null)}>Cancel</Button>
            <Button onClick={savePlan} disabled={!planForm.name || planForm.price === ""} data-testid="save-plan-def" className="bg-[#4338CA] hover:bg-[#3730A3]">Save plan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Manage plan · {edit?.organization_name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="mb-1 block normal-case tracking-normal text-slate-600">Plan</Label>
              <Select value={form.plan_id} onValueChange={onPlan}>
                <SelectTrigger data-testid="plan-select"><SelectValue placeholder="Choose plan" /></SelectTrigger>
                <SelectContent>{plans.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · R{p.price}/mo</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-1 block normal-case tracking-normal text-slate-600">Monthly recurring revenue (ZAR)</Label>
              <Input type="number" value={form.mrr} onChange={(e) => setForm({ ...form, mrr: e.target.value })} data-testid="plan-mrr" />
              <p className="mt-1 text-xs text-slate-400">Defaults to the plan's list price; override for custom/negotiated deals.</p>
            </div>
            <div>
              <Label className="mb-1 block normal-case tracking-normal text-slate-600">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="plan-status"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="cancelled">Cancelled</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEdit(null)}>Cancel</Button>
            <Button onClick={save} disabled={!form.plan_id} data-testid="save-plan" className="bg-[#4338CA] hover:bg-[#3730A3]">Save changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
